class PolicyRouterService {
  #policyRouterAxiosInstance;
  #consumerAppId;

  constructor(policyRouterAxiosInstance, consumerAppId) {
    if (!policyRouterAxiosInstance) {
      throw new Error('Axios instance is required');
    }
    if (!consumerAppId) {
      throw new Error('Consumer App ID is required');
    }
    this.#policyRouterAxiosInstance = policyRouterAxiosInstance;
    this.#consumerAppId = consumerAppId;
  }


/**
 * Searches policy router logs based on provided criteria.
 *
 * @param {object} basicLogSearchRequest
 * @param {string|null} [basicLogSearchRequest.appId] - Application identifier
 * @param {string|null} [basicLogSearchRequest.user] - User initiating the request
 * @param {string|null} [basicLogSearchRequest.transactionId] - Correlation/transaction ID
 * @param {string|null} [basicLogSearchRequest.startDate] - ISO start date (UTC)
 * @param {string|null} [basicLogSearchRequest.endDate] - ISO end date (UTC)
 * @param {string|null} [basicLogSearchRequest.serviceName] - Name of the service
 * @param {string|null} [basicLogSearchRequest.requestType] - Type of request made
 * @param {{ message: string, logLevel: string }|null} [basicLogSearchRequest.messages] - Log message filter
 * @param {string|null} [basicLogSearchRequest.clientNumber] - Client number
 * @param {string|null} [basicLogSearchRequest.clientName] - Client name
 * @param {string|null} [basicLogSearchRequest.dataSource] - Source system
 * @param {string|null} [basicLogSearchRequest.policyNumber] - Policy number
 * @param {string|null} [basicLogSearchRequest.firstName] - Insured first name
 * @param {string|null} [basicLogSearchRequest.lastName] - Insured last name
 * @param {string|null} [basicLogSearchRequest.state] - State code
 * @param {string|null} [basicLogSearchRequest.zipCode] - ZIP or postal code
 * @param {string|null} [basicLogSearchRequest.vin] - Vehicle VIN
 * @param {string} [basicLogSearchRequest.orderBy] - Field to order results by
 * @param {string} [basicLogSearchRequest.sortOrder='desc'] - Sort direction (asc | desc)
 * @param {number} [basicLogSearchRequest.limit=25] - Max number of records to return
 * @param {number} [basicLogSearchRequest.offset=0] - Pagination offset
 *
 * @returns {Promise<{ count: number, data: Array }>}
 */
  searchPolicyRouterLogsAdvanced({
    appId = null,
    user = null,
    transactionId = null,
    startDate = null,
    endDate = null,
    serviceName = null,
    requestType = null,
    messages = null,
    clientNumber = null,
    clientName = null,
    dataSource = null,
    policyNumber = null,
    firstName = null,
    lastName = null,
    state = null,
    zipCode = null,
    vin = null,
    orderBy = null,
    sortOrder = 'desc',
    limit = null,
    offset = null,
  } = {}) {
    var clean = function (obj) {
      return Object.fromEntries(
        Object.entries(obj).filter(function (entry) {
          return entry[1] !== null && entry[1] !== undefined;
        })
      );
  };

    return this.#policyRouterAxiosInstance.post('/aiSearch', clean({
        appId,
        user,
        transactionId,
        startDate,
        endDate,
        serviceName,
        requestType,
        messages,
        clientNumber,
        clientName,
        dataSource,
        policyNumber,
        firstName,
        lastName,
        state,
        zipCode,
        vin,
        orderBy,
        sortOrder,
        limit,
        offset,
    }), {
      headers: {
        'X-App-Id': this.#consumerAppId,
      },
    });
  }

  /**
 * Searches policy router logs based on provided criteria.
 *
 * @param {object} basicLogSearchRequest
 * @param {string|null} [basicLogSearchRequest.appId] - Application identifier
 * @param {string|null} [basicLogSearchRequest.user] - User initiating the request
 * @param {string|null} [basicLogSearchRequest.transactionId] - Correlation/transaction ID
 * @param {string|null} [basicLogSearchRequest.startDate] - ISO start date (UTC)
 * @param {string|null} [basicLogSearchRequest.endDate] - ISO end date (UTC)
 * @param {string|null} [basicLogSearchRequest.serviceName] - Name of the service
 * @param {string|null} [basicLogSearchRequest.requestType] - Type of request made
 * @param {{ message: string, logLevel: string }|null} [basicLogSearchRequest.messages] - Log message filter
 * @param {string|null} [basicLogSearchRequest.clientNumber] - Client number
 * @param {string|null} [basicLogSearchRequest.clientName] - Client name
 * @param {string|null} [basicLogSearchRequest.dataSource] - Source system
 * @param {string|null} [basicLogSearchRequest.policyNumber] - Policy number
 * @param {string|null} [basicLogSearchRequest.firstName] - Insured first name
 * @param {string|null} [basicLogSearchRequest.lastName] - Insured last name
 * @param {string|null} [basicLogSearchRequest.state] - State code
 * @param {string|null} [basicLogSearchRequest.zipCode] - ZIP or postal code
 * @param {string|null} [basicLogSearchRequest.vin] - Vehicle VIN
 * @param {string} [basicLogSearchRequest.orderBy] - Field to order results by
 * @param {string} [basicLogSearchRequest.sortOrder='desc'] - Sort direction (asc | desc)
 * @param {number} [basicLogSearchRequest.limit=25] - Max number of records to return
 * @param {number} [basicLogSearchRequest.offset=0] - Pagination offset
 *
 * @returns {Promise<{ count: number, data: Array }>}
 */
  searchPolicyRouterLogsBasic({
    appId = null,
    user = null,
    transactionId = null,
    startDate = null,
    endDate = null,
    serviceName = null,
    requestType = 'PolicySearch',
    clientNumber = null,
    dataSource = null,
    policyNumber = null,
    vin = null,
    orderBy = null,
    sortOrder = 'desc',
    limit = null,
  } = {}) {
    return this.#policyRouterAxiosInstance.post('/aiSearch', {
        appId,
        user,
        transactionId,
        startDate,
        endDate,
        serviceName,
        requestType,
        clientNumber,
        dataSource,
        policyNumber,
        vin,
        orderBy,
        sortOrder,
        limit,
    },{
      headers: {
        'X-App-Id': this.#consumerAppId,
      },
    });
  }
  
/**
 * Retrieves the list of Policy Router clients.
 * @returns {Promise<{data: object[]}>}
 */
  getPolicyRouterClients() {
    return this.#policyRouterAxiosInstance.get(`/clients`);
  }

/**
 * Retrieves the list of Policy Router clients names, client number, abbrv and status.
 * @returns {Promise<{data: object[]}>}
 */
  getPolicyRouterClientNames() {
    return this.#policyRouterAxiosInstance.get(`/clients`, {
      headers: {
        'X-Client-Config-View': 'minimal',
      },
    });
  }
  
  /**
   * Merges logs from different services based on transactionId.
   * @param {Array} payload - Array of log entries from various services.
   * @returns {Array} - Array of merged log entries grouped by transactionId.
   */
  mergeTransactionLogs(payload) {
      const mergedLogs = {};

      payload.results.forEach(log => {
        const txId = log.transactionId;

        // If this transactionId is not already in mergedLogs, create a new entry
        if (!mergedLogs[txId]) {
          mergedLogs[txId] = {
            transactionId: txId,
            timestamp: log.timestamp,
            datestamp: log.datestamp,
            clientNumber: log.clientNumber || null,
            clientName: log.clientName || null,
            dataSource: log.dataSource || null,
            messages: [],
            policyRouterLog: null,
            policyWrapperApiLog: null,
            policyLookupApiLog: null
          };
        }

        const merged = mergedLogs[txId];

        // Determine log type by serviceName
        if (log.serviceName === "PolicyRouterAPI" || log.policyRouterLog) {
          merged.policyRouterLog = log.policyRouterLog || log;
          // Fallback parent fields from router if available
          merged.timestamp = log.timestamp || merged.timestamp;
          merged.datestamp = log.datestamp || merged.datestamp;
          merged.clientNumber = log.clientNumber || merged.clientNumber;
          merged.clientName = log.clientName || merged.clientName;
        } else if (log.serviceName === "PolicyWrapperAPI") {
          if (!merged.policyWrapperApiLog ) {
            merged.policyWrapperApiLog = log.wrapperApiLog || log.policyWrapperApiLog;
          }else{
            // If we already have a wrapper log, we may want to merge messages or other relevant fields
            merged.policyWrapperApiLog.messages = [
              ...(merged.policyWrapperApiLog.messages || []),
              ...(log.wrapperApiLog?.messages || log.policyWrapperApiLog?.messages || [])
            ];
            // Override sourceRequest and sourceResponse if they exist in the new log, as they may contain different data
            merged.policyWrapperApiLog.sourceRequest = (!merged.policyWrapperApiLog.sourceRequest) ? log.wrapperApiLog?.sourceRequest : merged.policyWrapperApiLog.sourceRequest;
            merged.policyWrapperApiLog.sourceResponse = (!merged.policyWrapperApiLog.sourceResponse) ? log.wrapperApiLog?.sourceResponse : merged.policyWrapperApiLog.sourceResponse;
          }
        } else if (log.serviceName === "PolicyLookupAPI") {
          merged.policyLookupApiLog = log.policyLookupApiLog || log.policyLookupApiLog;
        }
      });

      // Return as array
      return Object.values(mergedLogs);
  }
}


export default PolicyRouterService;

